import { ReactNode } from "react";

import Link from "next/link";

import { Badge } from "@newsela/angelou";

import useAdminConsoleSidebarMenuOptions from "@/admin/hooks/useAdminConsoleSidebarMenuOptions";
import Sidebar from "@/components/Sidebar";
import formatHrefToSupportLegacyLink from "@/utils/formatHrefToSupportLegacyLink";
import { newBadgeCss } from "./AdminConsoleSidebar.style";

export interface AdminConsoleSidebarProps {
  /**
   * Object with the properties required to add the sub item into the sidebar when the user is in the record pages.
   */
  subMenuRecordInfo?:
    | {
        id: string;
        content: ReactNode;
        ariaLabel: string;
        title: string;
      }
    | undefined;
}

interface MenuItem {
  id: string;
  title: string;
  options: Array<{
    id: string;
    content: ReactNode;
    ariaLabel: string;
    title: string;
    url: string;
    relatedOptions: Array<{
      id: string;
      content: ReactNode;
      ariaLabel: string;
      title: string;
      url: string;
      status: object;
    }>;
  }>;
}

/**
 * AdminConsoleSidebar is a specific implementation of the Sidebar Component for the Admin Console.
 * The component receives the sub menu record that will be added to the sidebar when a user enters to the details page of
 * students, classrooms, faculty, and schools.
 */
const AdminConsoleSidebar = ({
  subMenuRecordInfo,
}: AdminConsoleSidebarProps) => {
  const sideBarData = useAdminConsoleSidebarMenuOptions({
    subMenuRecordInfo,
  }) as {
    menuItems: MenuItem[];
    selectedId: string;
    isLoadingNewMenuOption: boolean;
  };

  interface RenderCustomLinkProps {
    children: ReactNode;
    href: string;
  }

  /**
   * Method to use NextJS Link as a custom render function for the sidebar menu item.
   * The arguments are generated by the Angelou Anchor component, and are based on the menu Item definition.
   * @example
   * // returns <Link href="#"><a href="#" aria-label="Test" aria-current="true"><div>Test</div></a></Link>
   * clientRouterLink({
   *   aria-current: 'true',
   *   aria-label: 'Test',
   *   className: '',
   *   children: <div>Test</div>, //Code generated by the Angelou Anchor Component.
   *   href: '#',
   *   css: [{...}] //custom css array,
   *   title: 'Test'
   * });
   * @returns {Link} Returns a NextJs Link.
   */

  const renderCustomLink = ({
    children,
    href,
    ...anchorProps
  }: RenderCustomLinkProps) => {
    return (
      <Link href={formatHrefToSupportLegacyLink(href)} passHref legacyBehavior>
        <a {...anchorProps}>{children}</a>
      </Link>
    );
  };

  const renderNewBadge = () => {
    return (
      <Badge
        text="NEW"
        flavor={Badge.flavor.solid}
        color={Badge.color.blue}
        __cssFor={newBadgeCss}
        hideIcon
      />
    );
  };

  return (
    <Sidebar
      // @ts-expect-error Sidebar component is not converted to TS yet.
      menuItems={sideBarData.menuItems}
      selectedId={sideBarData.selectedId}
      __renderCustomLink={renderCustomLink}
      __renderNewBadge={renderNewBadge}
      isLoadingNewMenuOption={sideBarData.isLoadingNewMenuOption}
    />
  );
};

export default AdminConsoleSidebar;
